/**
 * Resolução do leitor de códigos.
 *
 * `BarcodeDetector` é nativo no Chrome/Edge/Android e ausente no Safari/iOS.
 * O ponyfill do pacote `barcode-detector` expõe exatamente a mesma API sobre
 * ZXing em WebAssembly, então o resto do app não sabe qual dos dois está
 * ativo — e o Android não paga o download do WASM.
 */

/**
 * Binário do leitor, emitido como asset local pelo Vite.
 *
 * Sem este redirecionamento, o zxing-wasm busca o `.wasm` em
 * `fastly.jsdelivr.net` em tempo de execução — e o leitor do iOS deixaria de
 * funcionar num evento com wifi ruim ou com o PWA offline, que é exatamente
 * onde o brechó vai operar.
 */
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

export interface CodigoDetectado {
  rawValue: string;
}

export interface DetectorCodigos {
  detect(fonte: CanvasImageSource): Promise<CodigoDetectado[]>;
}

type ConstrutorDetector = new (opcoes: { formats: string[] }) => DetectorCodigos;

/** Só QR: é o que a etiqueta imprime, e restringir acelera a detecção. */
const FORMATOS = ['qr_code'];

let cacheDetector: Promise<DetectorCodigos> | null = null;

function nativoDisponivel(): boolean {
  return typeof globalThis !== 'undefined' && 'BarcodeDetector' in globalThis;
}

async function construir(): Promise<DetectorCodigos> {
  if (nativoDisponivel()) {
    const Nativo = (globalThis as unknown as { BarcodeDetector: ConstrutorDetector })
      .BarcodeDetector;
    return new Nativo({ formats: FORMATOS });
  }

  const { BarcodeDetector, setZXingModuleOverrides } = await import('barcode-detector/ponyfill');

  setZXingModuleOverrides({
    locateFile: (caminho: string, prefixo: string) =>
      caminho.endsWith('.wasm') ? wasmUrl : `${prefixo}${caminho}`,
  });

  return new BarcodeDetector({ formats: FORMATOS as never }) as DetectorCodigos;
}

/**
 * O detector é caro de construir (o ponyfill carrega o WASM), então uma
 * instância serve todas as aberturas do leitor na mesma sessão.
 */
export function obterDetector(): Promise<DetectorCodigos> {
  cacheDetector ??= construir().catch((erro) => {
    // Não deixa uma falha de rede envenenar o cache: a próxima abertura tenta
    // de novo em vez de repetir o erro para sempre.
    cacheDetector = null;
    throw erro;
  });

  return cacheDetector;
}
